/**
 * Shared date/time helpers for public event page templates (JUB-127).
 *
 * The API always sends event dates as a bare calendar day ("YYYY-MM-DD",
 * `EventResource::date`) and ceremony times as ISO 8601 with a UTC offset
 * ("2027-06-19T12:00:00+03:00", `EventResource` ceremonies). In both cases
 * the digits written in the string ARE the wall clock the couple entered —
 * the event's own local time — and every template must render exactly that,
 * never a value shifted by the viewer's browser timezone.
 *
 * `formatEventDate` extracts those digits directly (ignoring any offset
 * suffix) and re-anchors them to UTC internally, then always formats with
 * `timeZone: "UTC"` so no runtime/browser timezone can shift what's shown.
 * Every template's local `formatDate` should delegate here instead of
 * re-implementing date parsing.
 */

const WALL_CLOCK_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * Parse an event/ceremony timestamp into a Date whose UTC components equal
 * the literal digits written in the string (offset, if any, is ignored on
 * purpose — see module docs). Returns null when the value is missing or
 * doesn't match the expected shape.
 */
export const parseWallClockDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const match = WALL_CLOCK_PATTERN.exec(value);
  if (!match) return null;
  const [, year, month, day, hour = "0", minute = "0", second = "0"] = match;
  const date = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)),
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Format an event/ceremony timestamp as the literal wall clock, regardless of viewer timezone. */
export const formatEventDate = (
  value: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  const wallClock = parseWallClockDate(value);
  if (!wallClock) return value ?? "";
  try {
    return new Intl.DateTimeFormat(locale === "ro" ? "ro-RO" : "en-US", {
      ...options,
      timeZone: "UTC",
    }).format(wallClock);
  } catch {
    return value ?? "";
  }
};

/** The calendar year written in an event/ceremony timestamp, as a string ("" when unparseable). */
export const getEventYear = (value: string | null | undefined): string => {
  const wallClock = parseWallClockDate(value);
  return wallClock ? String(wallClock.getUTCFullYear()) : "";
};

/**
 * JUB-94 made `shared/countdown.ts` the single implementation of countdown
 * arithmetic (it also owns the singular/plural label keys; since JUB-176 it
 * re-exports `src/lib/countdown.ts`, which also owns the calendar-day count,
 * `calendarDaysUntil`). This module keeps
 * re-exporting it so the date-formatting callers do not need to know that, and
 * so the two shared modules can never drift apart again.
 */
export type { CountdownValue as CountdownParts } from "./countdown";
export { calculateCountdown } from "./countdown";
